import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import * as ButtonEnums from './ButtonEnums';
import Tooltip from '../Tooltip/Tooltip';

// Size class definitions based on ButtonEnums
const sizeClasses = {
    [ButtonEnums.size.small]: 'h-[28px] text-[14px]',
    [ButtonEnums.size.medium]: 'h-[36px] text-[16px]',
};

// Base layout classes for the button
const layoutClasses =
    'box-content inline-flex flex-row items-center justify-center gap-[6px] px-[12px] outline-none rounded-lg';

// Base font text classes
const baseFontTextClasses = 'leading-[1.3] font-sans text-center whitespace-nowrap';

// Font text classes based on button type
const fontTextClasses = {
    [ButtonEnums.type.primary]: classnames(baseFontTextClasses, 'font-semibold'),
    [ButtonEnums.type.secondary]: classnames(baseFontTextClasses, 'font-normal'),
};

// Base enabled effect classes
const baseEnabledEffectClasses = 'transition duration-200 ease-in-out focus:outline-none';

// Enabled effect classes based on button type
const enabledEffectClasses = {
    [ButtonEnums.type.primary]: classnames(
        baseEnabledEffectClasses,
        'hover:bg-customblue-70 active:bg-customblue-50'
    ),
    [ButtonEnums.type.secondary]: classnames(
        baseEnabledEffectClasses,
        'hover:bg-customblue-40 active:bg-customblue-20'
    ),
};

// Base enabled classes
const baseEnabledClasses = 'text-white';

// Enabled classes based on button type
const enabledClasses = {
    [ButtonEnums.type.primary]: classnames(
        'bg-primary-main',
        baseEnabledClasses,
        enabledEffectClasses[ButtonEnums.type.primary]
    ),
    [ButtonEnums.type.secondary]: classnames(
        'bg-customblue-20',
        baseEnabledClasses,
        enabledEffectClasses[ButtonEnums.type.secondary]
    ),
};

// Disabled classes for the button
const disabledClasses = 'bg-inputfield-placeholder text-gray-400 cursor-not-allowed';

// Default prop values
const defaults = {
    size: ButtonEnums.size.medium,
    disabled: false,
    type: ButtonEnums.type.primary,
};

const Button = ({
    children = '',
    size = defaults.size,
    disabled = defaults.disabled,
    type = defaults.type,
    startIcon: startIconProp,
    endIcon: endIconProp,
    name,
    className,
    onClick = (e: any) => { },
    dataCY,
    startIconTooltip = null,
    endIconTooltip = null,
}) => {
    // Generate data-cy attribute for testing
    dataCY = dataCY || `${name}-btn`;

    // Prepare start and end icons with provided classNames
    const startIcon = startIconProp && (
        <>
            {React.cloneElement(startIconProp, {
                className: classnames('w-5 h-5 fill-current', startIconProp?.props?.className),
            })}
        </>
    );

    const endIcon = endIconProp && (
        <>
            {React.cloneElement(endIconProp, {
                className: classnames('w-5 h-5 fill-current', endIconProp?.props?.className),
            })}
        </>
    );

    const buttonElement = useRef(null);

    // Handle button click event
    const handleOnClick = e => {
        buttonElement.current.blur();
        if (!disabled) {
            onClick(e);
        }
    };

    // Final className for the button
    const finalClassName = classnames(
        layoutClasses,
        fontTextClasses[type],
        disabled ? disabledClasses : enabledClasses[type],
        sizeClasses[size],
        children ? 'min-w-[36px]' : '', // minimum width for buttons with text
        className
    );

    return (
        <button
            className={finalClassName}
            disabled={disabled}
            ref={buttonElement}
            onClick={handleOnClick}
            data-cy={dataCY}
        >
            {startIconTooltip ? <Tooltip content={startIconTooltip}>{startIcon}</Tooltip> : startIcon}
            {children}
            {endIconTooltip ? <Tooltip content={endIconTooltip}>{endIcon}</Tooltip> : endIcon}
        </button>
    );
};

Button.propTypes = {
    /** Content inside the button, can be text or a React component */
    children: PropTypes.node,
    /** Callback for button click */
    onClick: PropTypes.func,
    /** Button size */
    size: PropTypes.oneOf([ButtonEnums.size.medium, ButtonEnums.size.small]),
    /** Disabled state of the button */
    disabled: PropTypes.bool,
    /** Button type */
    type: PropTypes.oneOf([ButtonEnums.type.primary, ButtonEnums.type.secondary]),
    /** Name of the button, used for data-cy attribute */
    name: PropTypes.string,
    /** Icon component to be displayed at the start */
    startIcon: PropTypes.node,
    /** Icon component to be displayed at the end */
    endIcon: PropTypes.node,
    /** Additional TailwindCSS class names */
    className: PropTypes.string,
    /** Tooltip for the start icon */
    startIconTooltip: PropTypes.node,
    /** Tooltip for the end icon */
    endIconTooltip: PropTypes.node,
    /** Data attribute for testing */
    dataCY: PropTypes.string,
};

export default Button;
